// Theme Configuration
import { colors } from './colors';
import { typography } from './typography';
import { spacing } from './spacing';

export const theme = {
  colors,
  typography,
  spacing,
  
  // Breakpoints
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  // Z-index scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
  
  // Animation durations
  duration: {
    fastest: '100ms',
    faster: '150ms',
    fast: '200ms',
    normal: '300ms',
    slow: '500ms',
    slower: '700ms',
    slowest: '1000ms',
  },
  
  // Animation easing
  easing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
  
  // Component themes
  components: {
    button: {
      sizes: {
        sm: {
          padding: spacing.component.button.sm,
          fontSize: typography.fontSize.sm[0],
          borderRadius: spacing.radius.md,
        },
        md: {
          padding: spacing.component.button.md,
          fontSize: typography.fontSize.base[0],
          borderRadius: spacing.radius.md,
        },
        lg: {
          padding: spacing.component.button.lg,
          fontSize: typography.fontSize.lg[0],
          borderRadius: spacing.radius.lg,
        },
        xl: {
          padding: spacing.component.button.xl,
          fontSize: typography.fontSize.xl[0],
          borderRadius: spacing.radius.lg,
        },
      },
      variants: {
        primary: {
          backgroundColor: colors.primary[500],
          color: colors.text.inverse,
          borderColor: colors.primary[500],
          '&:hover': {
            backgroundColor: colors.primary[600],
            borderColor: colors.primary[600],
          },
          '&:focus': {
            boxShadow: `0 0 0 3px ${colors.primary[200]}`,
          },
        },
        secondary: {
          backgroundColor: colors.background.secondary,
          color: colors.text.primary,
          borderColor: colors.border.light,
          '&:hover': {
            backgroundColor: colors.secondary[600],
            borderColor: colors.secondary[600],
          },
          '&:focus': {
            boxShadow: `0 0 0 3px ${colors.secondary[200]}`,
          },
        },
        outline: {
          backgroundColor: 'transparent',
          color: colors.primary[500], // Indigo
          borderColor: colors.primary[500],
          '&:hover': {
            backgroundColor: colors.primary[500],
            color: colors.text.inverse,
          },
          '&:focus': {
            boxShadow: `0 0 0 3px ${colors.primary[200]}`,
          },
        },
        ghost: {
          backgroundColor: 'transparent',
          color: colors.text.secondary,
          borderColor: 'transparent',
          '&:hover': {
            backgroundColor: colors.gray[100],
          },
          '&:focus': {
            boxShadow: `0 0 0 3px ${colors.secondary[200]}`,
          },
        },
      },
    },
    
    card: {
      base: {
        backgroundColor: colors.background.card, // White cards
        borderRadius: spacing.radius.md,
        border: `1px solid ${colors.border.light}`,
        boxShadow: spacing.shadow.md,
        padding: '20px', // Generous padding
      },
      variants: {
        elevated: {
          backgroundColor: colors.background.elevated,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
        outlined: {
          border: `2px solid ${colors.border.medium}`,
          boxShadow: 'none',
        },
        filled: {
          backgroundColor: colors.gray[50],
          border: 'none',
          boxShadow: 'none',
        },
      },
    },
    
    input: {
      base: {
        backgroundColor: colors.background.secondary, // White background
        border: `1px solid ${colors.border.light}`,
        borderRadius: spacing.radius.md,
        color: colors.text.primary,
        '&:focus': {
          borderColor: colors.primary[500], // Indigo focus
          boxShadow: `0 0 0 3px ${colors.primary[200]}`,
        },
        '&:disabled': {
          backgroundColor: colors.gray[50],
          color: colors.text.tertiary,
        },
      },
      variants: {
        error: {
          borderColor: colors.danger[500], // Red for errors
          '&:focus': {
            borderColor: colors.danger[500],
            boxShadow: `0 0 0 3px ${colors.danger[200]}`,
          },
        },
        success: {
          borderColor: colors.success[500], // Green for success
          '&:focus': {
            borderColor: colors.success[500],
            boxShadow: `0 0 0 3px ${colors.success[200]}`,
          },
        },
      },
    },
  },
};

// Theme utilities
export const getThemeValue = (path, fallback = null) => {
  const keys = path.split('.');
  let value = theme;
  
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      return fallback;
    }
  }
  
  return value;
};

export const createVariant = (component, variant, size = 'md') => {
  const baseStyles = theme.components[component]?.base || {};
  const variantStyles = theme.components[component]?.variants?.[variant] || {};
  const sizeStyles = theme.components[component]?.sizes?.[size] || {};
  
  return {
    ...baseStyles,
    ...sizeStyles,
    ...variantStyles,
  };
};

export default theme;
